import React, { useMemo } from 'react';
import { useDispatch } from 'react-redux';
import {
  Button,
  ContextualMenu,
  IconEllipsisVertical,
  MenuItem,
  MenuList,
  useTheme
} from '@veneer/core';

import { useDependencyManagerContext } from 'src/contexts/dependencyManager';
import { postDeviceSharingAction } from 'src/features/shared-access/shared-access-slice';
import { DeviceSharingRequestAction } from 'src/types/devices';

export type ActionMenuProps = {
  sharingRequestId: string;
  deviceFamily: string;
  serialNumber: string;
  email: string;
  onExtend: () => void;
};

const ActionMenu: React.FC<ActionMenuProps> = (props: ActionMenuProps) => {
  const { sharingRequestId, deviceFamily, serialNumber, email, onExtend } =
    props;
  const { translate } = useDependencyManagerContext();
  const theme = useTheme();
  const dispatch = useDispatch();

  const { extendAnalyticsAttributes, revokeAnalyticsAttributes } = useMemo(
    () => ({
      extendAnalyticsAttributes: {
        'data-gtm-category': 'linkClick',
        'data-gtm-id': 'shared-devices-extend-access',
        'data-gtm-value': 'extend-access'
      },
      revokeAnalyticsAttributes: {
        'data-gtm-category': 'linkClick',
        'data-gtm-id': 'shared-devices-revoke-access',
        'data-gtm-value': 'revoke-access'
      }
    }),
    []
  );

  return (
    <ContextualMenu
      placement="bottom-end"
      anchorNode={
        <Button
          appearance="ghost"
          small
          leadingIcon={<IconEllipsisVertical />}
          customStyle={{ color: theme.color.foreground.medium }}
          aria-label={translate(
            'components.shared-devices-table.action-menu.aria-label',
            { deviceFamily, serialNumber }
          )}
          data-testid={`react-device-management-mfe-shared-devices-table-action-menu-${sharingRequestId}`}
        />
      }
    >
      <MenuList>
        <MenuItem
          label={translate('components.shared-devices-table.actions.extend')}
          value={''}
          data-testid={`react-device-management-mfe-shared-devices-table-action-menu-extend-${sharingRequestId}`}
          onClick={() => {
            onExtend();
            dispatch(
              postDeviceSharingAction({
                id: sharingRequestId,
                action: DeviceSharingRequestAction.EXTEND,
                email
              })
            );
          }}
          {...extendAnalyticsAttributes}
        />
        <MenuItem
          label={translate('components.shared-devices-table.actions.revoke')}
          value={''}
          data-testid={`react-device-management-mfe-shared-devices-table-action-menu-revoke-${sharingRequestId}`}
          onClick={() =>
            dispatch(
              postDeviceSharingAction({
                id: sharingRequestId,
                action: DeviceSharingRequestAction.REVOKE,
                email
              })
            )
          }
          customStyle={{
            '& > *:first-of-type': { color: theme.color.negative.base.default }
          }}
          {...revokeAnalyticsAttributes}
        />
      </MenuList>
    </ContextualMenu>
  );
};

export default ActionMenu;
